import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import injuryReportsResources from '@/modules/applications/injury-reports/resources'

import { INJURY_REPORT_STATUS_IDS } from '../constants'
import queries from '../queries'
import type { InjuryReport } from '../types'

import InjuryReportListItem from './InjuryReportListItem'

const InjuryReportList: FC<{
  searchFilter: string
  statusFilter: number
}> = ({ searchFilter, statusFilter }) => {
  const translate = useTranslation()
  const [queryAssetId] = useSearchParamsByKey('assetId')
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof itemsFiltered)[number]>()
  const { locale } = useAppContext()

  const { data: injuryReportList = [], isLoading } = useQuery(
    queries.injuryReports(),
    injuryReportsResources.getList
  )

  const getFilteredList = () => {
    if (statusFilter === INJURY_REPORT_STATUS_IDS.ALL && !searchFilter)
      return injuryReportList.filter(report =>
        queryAssetId ? report.assetId === Number(queryAssetId) : true
      )
    if (statusFilter === INJURY_REPORT_STATUS_IDS.ALL) {
      return injuryReportList.filter(report => {
        const isAssetIdFilterMet = queryAssetId ? report.assetId === Number(queryAssetId) : true
        return isAssetIdFilterMet && getQueryFilter(searchFilter, stringMatcher)
      })
    }

    return injuryReportList.filter(report => {
      const isAssetIdFilterMet = queryAssetId ? report.assetId === Number(queryAssetId) : true
      return (
        isAssetIdFilterMet &&
        report.statusId === statusFilter &&
        getQueryFilter(searchFilter, stringMatcher)(report)
      )
    })
  }
  const stringMatcher = (item: InjuryReport) => [
    item.userName,
    item.assetDescription,
    translate(`injury_report_status/${status}`),
  ]

  const itemsFiltered = getFilteredList()

  const sortedList = getSortedList({
    list: itemsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)

  if (isLoading) return <LoaderCircle />

  if (visibleList.length === 0) return <NoResults />

  return (
    <>
      <ResultCount count={itemsFiltered.length} />

      <Table rowHeight="medium">
        <thead>
          <tr>
            <th> </th>
            <Th id="userName" onClick={handleSort}>
              <Translation id="user" />
            </Th>
            <Th id="assetDescription" onClick={handleSort}>
              <Translation id="asset" />
            </Th>
            <Th id="status" onClick={handleSort}>
              <Translation id="status" />
            </Th>
            <Th id="injuryDate" onClick={handleSort}>
              <Translation id="injury date" />
            </Th>
            <Th id="created" onClick={handleSort}>
              <Translation id="created" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <InjuryReportListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>

      <ListLoaderComponent />
    </>
  )
}

export default InjuryReportList
